import type {
  PerfHaloExportRequest,
  PerfHaloReportView,
  ReportQueryRequest,
} from '@/api/types';
import type {
  PerfHaloReportSearch,
  PerfHaloReportTab,
} from '@/lib/perf-halo-report-search';

export type PerfHaloStandardTab = Exclude<PerfHaloReportTab, 'haloAnalysis'>;

/** Canonical grouping behind each non-Halo-Analysis tab. */
export const PERF_HALO_TAB_GROUPS: Record<
  PerfHaloStandardTab,
  ReportQueryRequest['group_by']
> = {
  overview: ['assigned_production'],
  campaign: ['campaign', 'assigned_production', 'channel'],
  production: ['assigned_production'],
  channel: ['channel'],
  audience: ['audience'],
  audienceType: ['audience_type'],
  creative: ['creative', 'assigned_production'],
};

/** Public CSV view names are wire values, not presentation-tab spellings. */
export const PERF_HALO_EXPORT_VIEWS: Record<PerfHaloReportTab, PerfHaloReportView> = {
  overview: 'overview',
  campaign: 'campaign',
  production: 'assigned_production',
  channel: 'channel',
  audience: 'audience',
  audienceType: 'audience_type',
  creative: 'creative',
  haloAnalysis: 'halo_analysis',
};

export const PERF_HALO_ANALYSIS_GROUPS = [
  'assigned_production',
  'sold_production',
  'audience_type',
] as const satisfies ReportQueryRequest['group_by'];

export function isPerfHaloAnalysisTab(
  tab: PerfHaloReportTab,
): tab is 'haloAnalysis' {
  return tab === 'haloAnalysis';
}

/** Translate validated URL state into the one approved filter shape. */
export function buildPerfHaloReportFilters(
  search: PerfHaloReportSearch,
): ReportQueryRequest['filters'] {
  return {
    assigned_production_ids: search.productionId ? [search.productionId] : [],
    sold_production_ids: [],
    campaign_ids: search.campaignId ? [search.campaignId] : [],
    audience_ids: search.audienceId ? [search.audienceId] : [],
    audience_revision: search.audienceRevision ?? null,
    creative_ids: search.creativeId ? [search.creativeId] : [],
    channels: search.channel ? [search.channel] : [],
    audience_types: search.audienceType ? [search.audienceType] : [],
    targets: search.target ? [search.target] : [],
    placements: [],
    currencies: ['USD'],
    provider_ad_ids: [],
    provider_creative_ids: [],
    attribution_effects: [],
    attribution_settings: [],
  };
}

/**
 * Canonical all-effects request for the standard tables, trend, channel mix,
 * and filter-option discovery. Explicit scope is an integrity invariant.
 */
export function buildPerfHaloReportRequest(
  search: PerfHaloReportSearch,
  groupBy: ReportQueryRequest['group_by'],
  filters: ReportQueryRequest['filters'],
  limit: number | null = 500,
  ordered = true,
): ReportQueryRequest {
  return buildRequest(search, 'performance', groupBy, filters, limit, ordered);
}

/** One canonical attribution result powers every Halo Analysis visualization. */
export function buildPerfHaloAnalysisRequest(
  search: PerfHaloReportSearch,
  filters: ReportQueryRequest['filters'],
  limit: number | null = 500,
  ordered = true,
): ReportQueryRequest {
  const request = buildRequest(
    search,
    'attribution',
    [...PERF_HALO_ANALYSIS_GROUPS],
    filters,
    limit,
    ordered,
  );
  if (ordered) {
    request.order = {
      field: search.haloMetric === 'tickets'
        ? 'attributed_conversions'
        : 'attributed_revenue',
      direction: 'desc',
    };
  }
  return request;
}

/** Specialized CSV body; the server independently forces the same invariants. */
export function buildPerfHaloExportRequest(
  search: PerfHaloReportSearch,
  filters: ReportQueryRequest['filters'] = buildPerfHaloReportFilters(search),
): PerfHaloExportRequest {
  const orderField = search.tab === 'haloAnalysis'
    ? search.haloMetric === 'tickets'
      ? 'attributed_conversions'
      : 'attributed_revenue'
    : search.sortField;
  return {
    view: PERF_HALO_EXPORT_VIEWS[search.tab],
    date_from: search.from,
    date_to: search.to,
    filters,
    order: {
      field: orderField,
      direction: search.tab === 'haloAnalysis' ? 'desc' : search.sortDirection,
    },
    halo_metric: search.haloMetric,
  };
}

function buildRequest(
  search: PerfHaloReportSearch,
  dataset: ReportQueryRequest['dataset'],
  groupBy: ReportQueryRequest['group_by'],
  filters: ReportQueryRequest['filters'],
  limit: number | null,
  ordered: boolean,
): ReportQueryRequest {
  return {
    dataset,
    attribution_scope: 'all_effects',
    date_from: search.from,
    date_to: search.to,
    group_by: groupBy,
    filters,
    order: ordered
      ? { field: search.sortField, direction: search.sortDirection }
      : null,
    cursor: null,
    limit,
  };
}
